import { LitElement, html, css } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import {repeat} from 'lit/directives/repeat.js';
import '../../voyage-popup/src/voyage-popup';
import '../../voy-timeinput/src/voy-timeinput';
import { TerminalsModel } from '../../../src/models/terminals-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { TerminalInfo } from '../../../src/models/types/terminals-model.types';

interface SearchResult {
    text: string;
    terminal: TerminalInfo;
}

@customElement('voy-terminal-input')
export class VoyTerminalInput extends LitElement
{
    @property({attribute: false}) terminalId: string = "";
    @property({attribute: false}) label: string = "";
    @property({attribute: false}) showTerminalsCode?: boolean;
    @property({attribute: false}) readonly: boolean = false;
    @property({attribute: false}) invalid: boolean = false;
    @property({attribute: false}) private _dropdownExpanded: boolean = false;
    @property({attribute: false}) private _dropdownIsCollapsing: boolean = false;
    @property({attribute: false}) private _shownList: SearchResult[] = [];
    @property({attribute: false}) private _selectedTerminal: TerminalInfo | null = null;

    private _terminalNameMap: Map<string, string> = new Map<string, string>();
    private _terminalDropdownData: SearchResult[] = [];

    constructor() {
        super();
        this._populateTerminalsData = this._populateTerminalsData.bind(this);
        this._applyFilter = this._applyFilter.bind(this);
    }

    private _populateTerminalsData(){
        const terminalDropdownData = [];
        for (const [_, value] of TerminalsModel!.terminals) {
            const text = this.showTerminalsCode ? `${value.locationId}` : `${value.city} (${value.location})`;
            this._terminalNameMap.set(value.locationId, text);
            terminalDropdownData.push({text: text, terminal: value});
        }
        this._terminalDropdownData = terminalDropdownData;
        this._applyFilter("");
    }

    static get styles()
    {
        return [
            css`

            .container {
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02);
                display: grid;
                grid-template-rows: auto auto;
            }

            .label {
                color: var(--functional-grey-800);
                margin-bottom: 8px;
            }

            .label[readonly] {
                color: var(--functional-grey-800);
            }

            .display {
                width: 100%;
                background: var(--neutrals-grey-10);
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 2.5px;
                display: grid;
                grid-template-columns: auto auto;
                place-content: start space-between;
                position: realtive;
            }

            .display[invalid] {
                border-color: var(--voyage-error-red, red);
            }

            .display[readonly] {
                background: var(--neutrals-grey-08);
                color: var(--neutrals-grey-04);
            }

            .displayed-terminal {
                padding: 12px;
                text-align: left;
                user-select: none;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .dropdown-svg {
                padding: 12px;
                cursor: pointer;
            }

            .dropdown-svg[readonly] {
                cursor: default;
            }

            .dropdown-svg > svg{
                color: var(--neutrals-grey-04, black);
                width: 9px;
                height: 14px;
                rotate: 90;
            }

            .dropdown-content {
                background: var(--neutrals-grey-10);
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 2.5px;
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                width: 100%;
          
                height: 386px;
                display: block;
                position: absolute;
                background: #FFFFFF;
                min-width: 222px;
                overflow: auto;
                z-index: 4;
            }

            .dropdown-content[hidden] {
                display: none;
            }

            .search-result {
                cursor: pointer;
                margin: 12px 16px;
                text-align: left;
                user-select: none;
            }

            .search-result:hover {
                background: rgba(0, 84, 135, 0.1);
            }

            .search-input {
                background: var(--neutrals-grey-10);
                border: 1px solid var(--neutrals-grey-06);
                border-radius: 2.5px;
                width: 100%;
                height: 36px;
            }

            .search-input[error] {
                background: var(--neutrals-grey-10);
                border: 1px solid var(--voyage-error-red, red);
                border-radius: 2.5px;
                width: 100%;
                height: 36px;
            }

            .search-input:focus,.search-input:active{
                outline: none;
            }

            .search-box-container {
                display: grid;
                place-items: center;
                margin: 15px auto;
                width: calc(100% - 20px);
            }
            .search{
                position: relative;
                width:100%;
            }

            .error-message {
                color: var(--voyage-error-red, red);
                font-family: MaerskText;
                font-size: 14px;
                margin: 12px 16px;
            }
        `
        ];
    }

    render()
    {
        return html`
        <div class="container">
            <div class="label" ?readonly=${this.readonly}>${this.label}</div>
            <div class="search">
                <div class="display" @mousedown=${(e: MouseEvent) => this._onDisplayClicked(e)} ?readonly=${this.readonly} ?invalid=${this.invalid}>
                    <div class="displayed-terminal" >${this._selectedTerminal !== null ? html`${this.getSelectedTerminalName()}` : html``}</div>
                    <div class="dropdown-svg" ?readonly=${this.readonly}>${this._dropdownExpanded ? html`${SvgIcons.lightChevronUp}` : html`${SvgIcons.lightChevronDown}`}</div>
                </div>
                <div id="dropdown" class="dropdown-content" ?hidden=${!this._dropdownExpanded}>
                    <div class="search-box-container">
                        <input type="text" placeholder=${this.showTerminalsCode ? 'Search GSIS' : 'Search..'} class="search-input" ?error=${this._shownList.length === 0} @input=${this.onSearchInput} @blur=${this._onSearchBlur}>
                    </div>
                    <div class="search-results-display">
                        ${this._shownList.length > 0 ? html`${repeat(this._shownList, i => i.terminal.locationId, (item, index) => html`
                            <div tabindex="-1" class="search-result">${item.text}</div>
                            `)}` : html `<p class="error-message">${this.showTerminalsCode ? 'Enter 7-digit code': 'Enter terminal name'}</p>`
                        }
                    </div>
                </div>
            </div>
        </div>
        `;
    }


    connectedCallback()
    {
        super.connectedCallback();
        this._populateTerminalsData();
        TerminalsModel!.addEventListener('terminals-updated', this._populateTerminalsData);
    }

    disconnectedCallback()
    {
        super.disconnectedCallback();
        TerminalsModel!.removeEventListener('terminals-updated', this._populateTerminalsData);
    }

    getSelectedTerminalName() {
        if (this._selectedTerminal) {
            return this._terminalNameMap.get(this._selectedTerminal.locationId);
        }

        return "";
    }

    private _onSearchBlur(e: FocusEvent) {
        // handle item clicked
        const dropdownItems = this.shadowRoot!.querySelector(".search-results-display");
        const itemClicked = dropdownItems!.contains(<HTMLElement>e.relatedTarget); // relatedTarget is the element to which the current element loses focus
        if (itemClicked) {
            this._selectedTerminal = this._shownList.filter((terminal) => terminal.text === (e.relatedTarget! as HTMLElement).textContent)[0].terminal;
            this.dispatchEvent(new CustomEvent<TerminalInfo>('terminal-selected', { detail: this._selectedTerminal }));
            this._dropdownExpanded = false;
            return;
        }
        // clicked outside dropdown, so collapse it
        this._dropdownExpanded = false;
    }

    private async _onDisplayClicked(e: MouseEvent) {
        // if dropdown is already expanded, do nothing
        if (this._dropdownExpanded) {
            return;
        }
        if (!this.readonly) {
            this._dropdownExpanded = true;
            const searchInput = (<HTMLElement>this.shadowRoot!.querySelector(".search-input"));
            await this.updateComplete; // wait for dropdown list to be visible in DOM
            searchInput!.focus();
            e.preventDefault()
        }
    }

    onSearchInput(evt: { target: { value: string; }; }) {
        const filterText = evt.target.value;
        this._applyFilter(filterText);
    }

    firstUpdated() {
        this._applyFilter("");
    }

    _applyFilter(filterText: string) {
        let shownList: SearchResult[] = this._terminalDropdownData;
        shownList = shownList.filter(item => item.text.toLocaleLowerCase().includes(filterText.toLocaleLowerCase()));

        this._shownList = shownList.sort((a, b) => {
            var nameA = a.text.toUpperCase();
            var nameB = b.text.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
          
            // names must be equal
            return 0;
          });
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
        if (changedProperties.has("terminalId")) {
            if (this.terminalId !== "") {
                this._selectedTerminal = TerminalsModel!.getTerminal(this.terminalId)!;
            } else {
                this._selectedTerminal = null;
            }
        }

        super.updated(changedProperties);
    }
};
